/* istanbul ignore file */ // framework loading code is implicitly tested enough that we do not need explicit tests

// Make types available.
import type {} from '../client/index';
import type { QueryClient } from '@tanstack/react-query';
import { IRMFrameworkClientConfig } from '@tempo-io/roadmapping-common-config';
import { RenderIntoProps } from '@rm3/components/platform/UIShell/Shell';
import { IRoadmapListPageConfig } from '@tempo-io/roadmapping-frontend-sidebar';

/**
 * A minimal shared module included in all bundles. It is used to provide access to shared state.
 * Uses a window global to share object references between instances of this module
 * and thus is able to provide the same object instances to all consuming components.
 */

export type CustomLocation = Location & {
	state: {
		comp?: {
			locationUri: string;
		};
	};
};

// SHOULDDO: Add actual types instead of using 'any'. - https://roadmunk.atlassian.net/browse/RMK-7404
interface IGlobalShared {
	window?: any;
	loader?: any;

	renderComponentInto?: any;

	vendorsPromise?: any;
	libPromise?: any;
	lib?: rmfT.ClientLibT;
	rmfFactoryPromise?: any;
	rmfFactory?: any;
	rmfPromise?: any;
	rmf?: rmfT.IRMClientFramework;
	shellPromise?: any;
	shell?: any;
	configPromise?: any;
	config?: IRMFrameworkClientConfig;

	_sharedInstanceId?: any;
	_vendorsLoadedTime?: any;
	_bootStartTime?: any;
	_rmfLoadedTime?: any;
	_libLoadedTime?: any;
	_shellLoadedTime?: any;
	_configLoadedTime?: any;
	_rmfInitializedTime?: any;
	_msToShellLoaded?: any;

	defaultQueryClient?: QueryClient;
	pageConfigs?: IRoadmapListPageConfig[];
}

declare global {
	interface Window {
		_rm3_shared: IGlobalShared;
		_rm3_bootstrapConfig?: any;
		_rm3_pageLoadTime?: any;
		rmf?: any; // Convenience shortcut available for 'envMode=local-dev' only. 'rmf' should not be so easily available for prod code and will be injected into components where needed.
	}
}

// Export shared reference object which is seeded in '@rm3/dist/browser/bootloader.bundle.js'
window._rm3_shared = window._rm3_shared || {};
export const shared = window._rm3_shared;

// The 'shared' instance is a singleton.
// This ID helps identify multiple instances when debugging in case something causes more than one to be created.
if (!shared._sharedInstanceId) {
	shared._sharedInstanceId = Date.now();
}

/**
 * Render a component bundle into the framework using Shell_UI.
 */
export function renderComponentInto(params: RenderIntoProps) {
	return shared.shellPromise
		.then(async () => {
			const { unmount, update } = await shared.shell.renderIntoFrameworkInternalOnly(params);
			return { unmount, update };
		})
		.catch((err) => {
			// SHOULDDO: Display error to user and log to server. - https://roadmunk.atlassian.net/browse/RMK-8463
			console.error(`[rm3] Error loading component '${params.componentId}' wrapped in UI Shell!`, err);
			// NOTE: We log error to ensure it always surfaces and then also throw so UI can show feedback in UI if applicable.
			throw err;
		});
}

window._rm3_shared.renderComponentInto = renderComponentInto;
